import React, { memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
} from '@mui/material';
import type { TimeSlot } from '../../../../domain/dailyShiftSlot';
import { TIME_SLOTS } from '../../../../domain/dailyShiftSlot';
import { EditableCell, ReadOnlyCell } from '../MonthlyShiftSlotSetup.styles';

interface TimeSlotTableProps {
  slots: TimeSlot[];
  isEditable: boolean;
  editedSlots: Map<string, Partial<TimeSlot>>;
  onSlotChange: (slotId: string, field: 'recruitmentSlots' | 'urgentSlots', value: number) => void;
}

/**
 * 時間帯テーブルコンポーネント
 *
 * - 16時間帯表示（07:00〜23:00）
 * - 募集医師枠数・急募医師枠数: 編集可能フィールド（赤背景）
 * - 募集済み枠数・診療予約枠(合計)・確定医師枠数・不足医師枠数: 表示のみ
 * - 確定ステータス時は編集不可
 */
const TimeSlotTable: React.FC<TimeSlotTableProps> = memo(({
  slots,
  isEditable,
  editedSlots,
  onSlotChange,
}) => {
  // TIME_SLOTSに基づいてスロットをマッピング
  const slotMap = new Map<string, TimeSlot>();
  slots.forEach(slot => {
    slotMap.set(`${slot.startTime}-${slot.endTime}`, slot);
  });

  const getSlotValue = (slot: TimeSlot, field: 'recruitmentSlots' | 'urgentSlots'): number => {
    const edited = editedSlots.get(slot.id);
    if (edited && edited[field] !== undefined) {
      return edited[field] as number;
    }
    return slot[field];
  };

  const handleChange = (
    slot: TimeSlot,
    field: 'recruitmentSlots' | 'urgentSlots',
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      onSlotChange(slot.id, field, value);
    }
  };

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>時間帯</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>
              募集医師枠数
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>
              募集済み枠数
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>
              診療予約枠(合計)
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>
              急募医師枠数
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>
              確定医師枠数
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>
              不足医師枠数
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {TIME_SLOTS.map(timeSlotDef => {
            const key = `${timeSlotDef.start}-${timeSlotDef.end}`;
            const slot = slotMap.get(key);

            if (!slot) {
              return (
                <TableRow key={key}>
                  <TableCell>{timeSlotDef.start}〜{timeSlotDef.end}</TableCell>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    データなし
                  </TableCell>
                </TableRow>
              );
            }

            return (
              <TableRow key={slot.id}>
                <TableCell sx={{ fontWeight: 500 }}>
                  {slot.startTime}〜{slot.endTime}
                </TableCell>

                {/* 募集医師枠数（編集可能） */}
                <EditableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={getSlotValue(slot, 'recruitmentSlots')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange(slot, 'recruitmentSlots', e)
                    }
                    disabled={!isEditable}
                    inputProps={{ min: 0, style: { textAlign: 'center' } }}
                    sx={{ width: 70 }}
                  />
                </EditableCell>

                {/* 募集済み枠数（表示のみ） */}
                <ReadOnlyCell>{slot.recruitedSlots}</ReadOnlyCell>

                {/* 診療予約枠(合計)（表示のみ） */}
                <ReadOnlyCell>{slot.totalReservationSlots}</ReadOnlyCell>

                {/* 急募医師枠数（編集可能） */}
                <EditableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={getSlotValue(slot, 'urgentSlots')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange(slot, 'urgentSlots', e)
                    }
                    disabled={!isEditable}
                    inputProps={{ min: 0, style: { textAlign: 'center' } }}
                    sx={{ width: 70 }}
                  />
                </EditableCell>

                {/* 確定医師枠数（表示のみ） */}
                <ReadOnlyCell>{slot.confirmedSlots}</ReadOnlyCell>

                {/* 不足医師枠数（表示のみ） */}
                <ReadOnlyCell>{slot.shortageSlots}</ReadOnlyCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

TimeSlotTable.displayName = 'TimeSlotTable';

export default TimeSlotTable;
